import { LabelMesh, CanvasLabelMesh, HTMLLabelMesh } from '.';
import { ScreenHTMLLabelMesh } from './ScreenHTMLLabelMesh';
import { ScreenHTMLStaticLabelMesh, ScreenPosition } from './ScreenHTMLStaticLabelMesh';
import { LabelMeshType, EnumUtils } from '../types';
import { POI } from '../poi/POI';

/**
 * LabelMeshFactory - Creates LabelMesh instances (Canvas or HTML)
 * Proper OOP Factory pattern - only creates mesh types, not entire labels!
 * Labels are instantiated by POI, this factory only provides the mesh
 */
export class LabelMeshFactory {
    
    /**
     * Create appropriate mesh type based on app settings or explicit type
     */
    public static createMesh(poi: POI, explicitType?: LabelMeshType): LabelMesh {
        // Determine mesh type using enum
        const meshType = explicitType || LabelMeshFactory.determineMeshType(poi);
        
        ////console.log(`🏭 [FACTORY] Creating for ${poi.name}: type=${meshType}`);
        
        // Create appropriate mesh instance using enum comparison
        if (meshType === LabelMeshType.CANVAS) {
            ////console.log(`🎨 [FACTORY] Creating CanvasLabelMesh for ${poi.name}`);
            return new CanvasLabelMesh(poi);
        } else if (meshType === LabelMeshType.HTML) {
            ////console.log(`🌐 [FACTORY] Creating HTMLLabelMesh for ${poi.name}`);
            return new HTMLLabelMesh(poi);
        } else if (meshType === LabelMeshType.SCREEN_HTML) {
            ////console.log(`📱 [FACTORY] Creating ScreenHTMLLabelMesh for ${poi.name}`);
            return new ScreenHTMLLabelMesh(poi);
        } else if (meshType === LabelMeshType.SCREEN_HTML_STATIC) {
            ////console.log(`📍 [FACTORY] Creating ScreenHTMLStaticLabelMesh for ${poi.name}`);
            const screenPosition = poi.poidata.labelScreenPosition?.value || ScreenPosition.TOP_RIGHT;
            return new ScreenHTMLStaticLabelMesh(poi, screenPosition);
        } else {
            // Default fallback
            console.warn(`⚠️ [FACTORY] Unknown mesh type ${meshType}, falling back to Canvas`);
            return new CanvasLabelMesh(poi);
        }
    }
    
    /**
     * Create Canvas mesh specifically
     */
    public static createCanvasMesh(poi: POI): CanvasLabelMesh {
        return new CanvasLabelMesh(poi);
    }
    
    /**
     * Create HTML mesh specifically
     */
    public static createHTMLMesh(poi: POI): HTMLLabelMesh {
        return new HTMLLabelMesh(poi);
    }
    
    /**
     * Determine mesh type based on poidata settings
     */
    private static determineMeshType(poi: POI): LabelMeshType {
        // The render mode is now on the poidata object itself
        const textRenderMode = poi.poidata.labelRenderMode?.value; 
        
        // Use enum utility for type-safe normalization
        const normalizedRenderMode = EnumUtils.normalizeTextRenderMode(textRenderMode ?? undefined);
        
        ////console.log(`🔍 [FACTORY] Determining type for ${poi.name}: renderMode="${textRenderMode}" → normalized="${normalizedRenderMode}" → ${normalizedRenderMode === 'canvas' ? 'CANVAS' : 'HTML'}`);
        
        // Route based on normalized render mode
        if (normalizedRenderMode === 'canvas') {
            return LabelMeshType.CANVAS;
        } else if (normalizedRenderMode === 'html') {
            return LabelMeshType.HTML;
        } else if (normalizedRenderMode === 'screen-html') {
            return LabelMeshType.SCREEN_HTML;
        } else if (normalizedRenderMode === 'screen-html-static') {
            return LabelMeshType.SCREEN_HTML_STATIC;
        } else {
            // Default fallback
            return LabelMeshType.CANVAS;
        }
    }
    
    /**
     * Get available mesh types
     */
    public static getAvailableMeshTypes(): LabelMeshType[] {
        return [LabelMeshType.CANVAS, LabelMeshType.HTML, LabelMeshType.SCREEN_HTML, LabelMeshType.SCREEN_HTML_STATIC];
    }
    
    /**
     * Check if a mesh type is supported
     */
    public static isMeshTypeSupported(type: string): boolean {
        return EnumUtils.isValidLabelMeshType(type);
    }
} 